import React from 'react';
import { EnvironmentOutlined, CalendarOutlined } from '@ant-design/icons';
import {
  TB_COLORS,
  statusMeta,
  priorityMeta,
  parseTags,
  tagColor,
  initials,
  dueMeta,
  avatarColor,
} from './utils';

export const Avatar = ({ name, size = 24 }) => (
  <span
    title={name}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      borderRadius: '50%',
      background: avatarColor(name),
      color: '#fff',
      fontSize: size * 0.42,
      fontWeight: 600,
      border: '1.5px solid #fff',
      flex: '0 0 auto',
    }}
  >
    {initials(name)}
  </span>
);

// A task has a single assignee, but render as a group for the Figma look.
export const Assignees = ({ task, size = 24 }) => {
  const name = task.assignee?.name;
  if (!name) return null;
  return <Avatar name={name} size={size} />;
};

export const PriorityDot = ({ priority, size = 8 }) => (
  <span
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: '50%',
      background: priorityMeta(priority).color,
      flex: '0 0 auto',
    }}
  />
);

export const StatusPill = ({ status }) => {
  const m = statusMeta(status);
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 16,
        background: m.pillBg,
        color: m.pillText,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {m.label}
    </span>
  );
};

export const TagPills = ({ tags, max = 3 }) => {
  const list = parseTags(tags);
  if (!list.length) return null;
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {shown.map((t) => {
        const c = tagColor(t);
        return (
          <span
            key={t}
            style={{
              padding: '2px 8px',
              borderRadius: 6,
              background: c.bg,
              color: c.color,
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {t}
          </span>
        );
      })}
      {extra > 0 ? (
        <span style={{ color: TB_COLORS.subtext, fontSize: 12, alignSelf: 'center' }}>+{extra}</span>
      ) : null}
    </span>
  );
};

export const DueLabel = ({ task }) => {
  const m = dueMeta(task.due_date, task.status);
  if (!m) return null;
  const color = m.overdue ? TB_COLORS.red : m.isToday ? TB_COLORS.orange : TB_COLORS.subtext;
  return (
    <span style={{ color, fontSize: 13, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <CalendarOutlined style={{ fontSize: 12 }} /> {m.label}
    </span>
  );
};

export const LocationLabel = ({ task, areaMap, propertyName }) => {
  const area = areaMap?.[task.primary_object_id];
  const parts = [propertyName, area].filter(Boolean);
  if (!parts.length) return null;
  return (
    <span style={{ color: TB_COLORS.subtext, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <EnvironmentOutlined style={{ fontSize: 12 }} /> {parts.join('  ·  ')}
    </span>
  );
};
